import type {
  CoachHealthDimensionResult,
  CoachHealthDimensionName,
  CoachHealthDimensionState,
  CoachHealthEvidenceProfile,
  CoachHealthEvidenceProfileActive,
  CoachHealthEvidenceProfileItem,
  CoachHealthPublicSourceIssue,
} from "@/lib/coachHealth/coachHealthEvidenceProfileContracts";
import {
  getCoachHealthEvidenceSourceDefinition,
  isCoachHealthRecordedContextSourceType,
} from "@/lib/coachHealth/coachHealthEvidenceSourceRegistry";
import type {
  CoachHealthCompactPresentation,
  CoachHealthPresentation,
  CoachHealthPresentationInput,
  CoachHealthWorkspaceDimensionPresentation,
  CoachHealthWorkspaceEvidencePresentation,
} from "@/lib/coachHealth/coachHealthPresentationContracts";
import { isCanonicalCoachHealthEvidenceRoute } from "@/lib/coachHealth/coachHealthEvidenceRoute";

const LABEL = "Your coaching practice" as const;
const SOURCE_UNAVAILABLE = "Recorded context source unavailable" as const;
const EVIDENCE_DESTINATION_UNAVAILABLE =
  "This coaching context is no longer available." as const;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const CANONICAL_UTC_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3}|\d{6})Z$/;
const UUID_FRAGMENT_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
const HUMAN_NAME_PATTERN =
  /^[\p{L}\p{M}][\p{L}\p{M}'’.-]*(?: [\p{L}\p{M}][\p{L}\p{M}'’.-]*){0,4}$/u;
const LEADER_LABEL_UNAVAILABLE = "Name unavailable" as const;

const DIMENSION_COPY: Readonly<
  Record<
    CoachHealthDimensionName,
    Pick<CoachHealthWorkspaceDimensionPresentation, "label">
  >
> = {
  philosophy_and_evidence: {
    label: "Philosophy and evidence",
  },
  support_and_challenge: {
    label: "Support and challenge",
  },
  continuity: {
    label: "Recorded context",
  },
  reflective_adaptation: {
    label: "Reflective adaptation",
  },
};

const DIMENSION_ORDER: readonly CoachHealthDimensionName[] = [
  "philosophy_and_evidence",
  "support_and_challenge",
  "continuity",
  "reflective_adaptation",
];

const TIMESTAMP_PREFIX = {
  coach_transcript_session_record: "Session record updated",
  coach_first_take: "First take updated",
  coach_reviewable_transcript: "Transcript ready",
  coaching_continuity_moment: "Continuity recorded",
} as const;

const PROFILE_STATES = new Set([
  "idle",
  "loading",
  "error",
  "no_leaders",
  "no_active_relationships",
  "access_unavailable",
  "ready",
  "partial",
]);
const DIMENSION_STATES = new Set([
  "evidence_available",
  "not_yet_available",
  "insufficient_recorded_context",
  "partial",
  "unavailable",
  "error",
]);
const DIMENSION_NAMES = new Set<CoachHealthDimensionName>(DIMENSION_ORDER);
const PRESENTATION_INPUT_KEYS = new Set([
  "profile",
  "recordedContextFractionEnabled",
  "evidenceActionsEnabled",
  "freshness",
  "leaderLabelsById",
  "timeZone",
]);
const PROFILE_KEYS = new Set([
  "version",
  "state",
  "asOf",
  "errorCode",
  "eligibleLeaderIds",
  "eligibleRelationshipKeys",
  "exclusionCounts",
  "sourceIssues",
  "dimensions",
  "recordedContextCoverage",
  "leaderPerspectiveState",
]);
const RELATIONSHIP_KEY_KEYS = new Set(["coachId", "leaderId"]);
const EXCLUSION_KEYS = new Set(["reason", "count"]);
const SOURCE_ISSUE_KEYS = new Set([
  "sourceType",
  "state",
  "publicLabel",
  "errorCode",
]);
const DIMENSION_KEYS = new Set([
  "dimension",
  "state",
  "matchingLeaderIds",
  "sourceIssues",
  "evidence",
]);
const COVERAGE_KEYS = new Set([
  "state",
  "matchingLeaderIds",
  "sourceIssues",
  "evidence",
]);
const EVIDENCE_KEYS = new Set([
  "sourceType",
  "sourceId",
  "evidenceAt",
  "leaderId",
  "relationshipRevision",
  "eventKey",
  "routeBindingId",
  "route",
]);
const EXCLUSION_REASONS = new Set([
  "access_unavailable",
  "cross_coach",
  "duplicate_event",
  "inactive_relationship",
  "pre_relationship",
  "relationship_not_eligible",
  "relationship_revision_mismatch",
  "source_lineage_invalid",
  "source_status_ineligible",
  "source_timestamp_invalid",
  "unauthorized_relationship",
  "unresolved_profile",
]);

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactOwnDataProperties(
  value: UnknownRecord,
  expectedKeys: ReadonlySet<string>,
): boolean {
  const keys = Reflect.ownKeys(value);
  return (
    keys.length === expectedKeys.size &&
    keys.every((key) => {
      if (typeof key !== "string" || !expectedKeys.has(key)) return false;
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      return Boolean(descriptor && "value" in descriptor);
    })
  );
}

function isDenseDataArray(value: unknown): value is readonly unknown[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) {
    return false;
  }
  const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
  if (
    !lengthDescriptor ||
    !("value" in lengthDescriptor) ||
    !Number.isSafeInteger(lengthDescriptor.value) ||
    lengthDescriptor.value < 0
  ) {
    return false;
  }
  const length = lengthDescriptor.value as number;
  const keys = Reflect.ownKeys(value);
  if (keys.length !== length + 1) return false;
  for (let index = 0; index < length; index += 1) {
    const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
    if (!descriptor || !("value" in descriptor)) return false;
  }
  return keys.every(
    (key) =>
      key === "length" ||
      (typeof key === "string" &&
        /^(0|[1-9]\d*)$/.test(key) &&
        Number(key) < length),
  );
}

type SnapshotResult =
  | { readonly ok: true; readonly value: unknown }
  | { readonly ok: false };

function snapshotOwnDataTree(value: unknown): SnapshotResult {
  if (Array.isArray(value)) {
    if (!isDenseDataArray(value)) return { ok: false };
    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (!lengthDescriptor || !("value" in lengthDescriptor)) return { ok: false };
    const snapshot: unknown[] = [];
    for (let index = 0; index < lengthDescriptor.value; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (!descriptor || !("value" in descriptor)) return { ok: false };
      const item = snapshotOwnDataTree(descriptor.value);
      if (!item.ok) return item;
      snapshot.push(item.value);
    }
    return { ok: true, value: snapshot };
  }
  if (typeof value !== "object" || value === null) {
    return { ok: true, value };
  }
  if (!isRecord(value)) return { ok: false };
  const snapshot = Object.create(Object.getPrototypeOf(value)) as UnknownRecord;
  for (const key of Reflect.ownKeys(value)) {
    if (typeof key !== "string") return { ok: false };
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor || !("value" in descriptor)) return { ok: false };
    const item = snapshotOwnDataTree(descriptor.value);
    if (!item.ok) return item;
    Object.defineProperty(snapshot, key, {
      configurable: true,
      enumerable: descriptor.enumerable,
      writable: true,
      value: item.value,
    });
  }
  return { ok: true, value: snapshot };
}

function isLeaderLabelRecord(value: unknown): value is Readonly<Record<string, string>> {
  if (!isRecord(value)) return false;
  return Reflect.ownKeys(value).every((key) => {
    if (typeof key !== "string" || !isUuid(key)) return false;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return Boolean(
      descriptor && "value" in descriptor && typeof descriptor.value === "string",
    );
  });
}

function authenticatedLeaderLabel(value: string | undefined): string {
  if (
    value === undefined ||
    value === "Leader" ||
    value.trim() !== value ||
    value.length === 0 ||
    value.length > 80 ||
    UUID_FRAGMENT_PATTERN.test(value) ||
    !HUMAN_NAME_PATTERN.test(value)
  ) {
    return LEADER_LABEL_UNAVAILABLE;
  }
  return value;
}

function isStringArray(value: unknown): value is readonly string[] {
  return isDenseDataArray(value) && value.every((item) => typeof item === "string");
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function isCanonicalUtcTimestamp(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = value.match(CANONICAL_UTC_TIMESTAMP_PATTERN);
  if (!match) return false;

  const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue] =
    match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  const second = Number(secondValue);
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, 0);

  return (
    year > 0 &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hour &&
    date.getUTCMinutes() === minute &&
    date.getUTCSeconds() === second
  );
}

function canonicalTimestampSortKey(value: string): string {
  const match = value.match(CANONICAL_UTC_TIMESTAMP_PATTERN);
  if (!match) return "";
  const fraction = match[7].padEnd(6, "0");
  return `${value.slice(0, 19)}.${fraction}Z`;
}

function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== "string" || value.trim() !== value || value.length === 0) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}

function isPresentationInput(value: unknown): value is CoachHealthPresentationInput {
  return (
    isRecord(value) &&
    hasExactOwnDataProperties(value, PRESENTATION_INPUT_KEYS) &&
    typeof value.recordedContextFractionEnabled === "boolean" &&
    typeof value.evidenceActionsEnabled === "boolean" &&
    (value.freshness === "fresh" || value.freshness === "refreshing") &&
    isLeaderLabelRecord(value.leaderLabelsById) &&
    isValidTimeZone(value.timeZone)
  );
}

function isSourceIssueArray(value: unknown): value is readonly CoachHealthPublicSourceIssue[] {
  return (
    isDenseDataArray(value) &&
    value.every((item) => {
      if (!isRecord(item) || !hasExactOwnDataProperties(item, SOURCE_ISSUE_KEYS)) {
        return false;
      }
      const definition = getCoachHealthEvidenceSourceDefinition(item.sourceType);
      return (
        definition !== null &&
        (item.state === "error" || item.state === "unavailable") &&
        item.publicLabel === definition.publicLabel &&
        item.errorCode === definition.publicErrorCode
      );
    })
  );
}

function isValidEvidenceEventKey(
  sourceType: string,
  sourceId: string,
  eventKey: unknown,
): boolean {
  if (typeof eventKey !== "string") return false;
  const match = eventKey.match(/^(coach_call|transcript):(.+)$/);
  if (!match || !isUuid(match[2])) return false;
  const [eventType, eventId] = [match[1], match[2]];

  switch (sourceType) {
    case "completed_coach_call":
      return eventType === "coach_call" && eventId === sourceId;
    case "coach_first_take":
      return eventType === "coach_call";
    case "coach_reviewable_transcript":
      return eventType === "coach_call" || eventId === sourceId;
    case "coach_transcript_session_record":
    case "coaching_continuity_moment":
      return true;
    default:
      return false;
  }
}

function isEvidenceItemArray(
  value: unknown,
  recordedContextOnly: boolean,
  asOf: string,
): boolean {
  const asOfSortKey = canonicalTimestampSortKey(asOf);
  return (
    isDenseDataArray(value) &&
    value.every((item) => {
      if (!isRecord(item) || !hasExactOwnDataProperties(item, EVIDENCE_KEYS)) {
        return false;
      }
      const definition = getCoachHealthEvidenceSourceDefinition(item.sourceType);
      const evidenceAtIsValid = isCanonicalUtcTimestamp(item.evidenceAt);
      return (
        definition !== null &&
        definition.sourceType !== "coach_philosophy" &&
        (!recordedContextOnly ||
          isCoachHealthRecordedContextSourceType(definition.sourceType)) &&
        isUuid(item.sourceId) &&
        evidenceAtIsValid &&
        canonicalTimestampSortKey(item.evidenceAt) <= asOfSortKey &&
        isUuid(item.leaderId) &&
        isUuid(item.routeBindingId) &&
        isUuid(item.relationshipRevision) &&
        isValidEvidenceEventKey(
          definition.sourceType,
          item.sourceId,
          item.eventKey,
        ) &&
        typeof item.route === "string"
      );
    })
  );
}

function sourceIssueSignature(issue: CoachHealthPublicSourceIssue): string {
  return `${issue.sourceType}:${issue.state}`;
}

function hasExactSourceIssues(
  left: readonly CoachHealthPublicSourceIssue[],
  right: readonly CoachHealthPublicSourceIssue[],
): boolean {
  const leftSignatures = left.map(sourceIssueSignature).sort();
  const rightSignatures = right.map(sourceIssueSignature).sort();
  return (
    new Set(leftSignatures).size === leftSignatures.length &&
    new Set(rightSignatures).size === rightSignatures.length &&
    leftSignatures.length === rightSignatures.length &&
    leftSignatures.every(
      (signature, index) => signature === rightSignatures[index],
    )
  );
}

function expectedRecordedContextState(
  hasEvidence: boolean,
  sourceIssues: readonly CoachHealthPublicSourceIssue[],
): CoachHealthDimensionState {
  if (hasEvidence && sourceIssues.length > 0) return "partial";
  if (hasEvidence) return "evidence_available";
  if (sourceIssues.some((issue) => issue.state === "error")) return "error";
  if (sourceIssues.some((issue) => issue.state === "unavailable")) {
    return "unavailable";
  }
  return "not_yet_available";
}

function matchingSetEqualsEvidenceLeaders(
  matchingLeaderIds: readonly string[],
  evidence: readonly CoachHealthEvidenceProfileItem[],
): boolean {
  const matching = new Set(matchingLeaderIds);
  const evidenceLeaders = new Set(evidence.map((item) => item.leaderId));
  return (
    matching.size === matchingLeaderIds.length &&
    matching.size === evidenceLeaders.size &&
    [...matching].every((leaderId) => evidenceLeaders.has(leaderId))
  );
}

function hasUniqueRelationshipEvents(
  evidence: readonly CoachHealthEvidenceProfileItem[],
): boolean {
  const keys = evidence.map((item) => `${item.leaderId}:${item.eventKey}`);
  return new Set(keys).size === keys.length;
}

function isPresentationProfile(value: unknown): value is CoachHealthEvidenceProfile {
  if (
    !isRecord(value) ||
    !hasExactOwnDataProperties(value, PROFILE_KEYS) ||
    value.version !== "coach_health_evidence_profile_v2" ||
    typeof value.state !== "string" ||
    !PROFILE_STATES.has(value.state) ||
    !isStringArray(value.eligibleLeaderIds) ||
    !value.eligibleLeaderIds.every(isUuid) ||
    !isDenseDataArray(value.eligibleRelationshipKeys) ||
    !value.eligibleRelationshipKeys.every(
      (item) =>
        isRecord(item) &&
        hasExactOwnDataProperties(item, RELATIONSHIP_KEY_KEYS) &&
        isUuid(item.coachId) &&
        isUuid(item.leaderId),
    ) ||
    !isDenseDataArray(value.exclusionCounts) ||
    !value.exclusionCounts.every(
      (item) =>
        isRecord(item) &&
        hasExactOwnDataProperties(item, EXCLUSION_KEYS) &&
        typeof item.reason === "string" &&
        EXCLUSION_REASONS.has(item.reason) &&
        typeof item.count === "number" &&
        Number.isSafeInteger(item.count) &&
        item.count > 0,
    ) ||
    !isSourceIssueArray(value.sourceIssues) ||
    !(value.asOf === null || isCanonicalUtcTimestamp(value.asOf))
  ) {
    return false;
  }

  const eligibleLeaderIds = new Set(value.eligibleLeaderIds);
  const relationshipSignatures = value.eligibleRelationshipKeys.map(
    (key) => `${key.coachId}:${key.leaderId}`,
  );
  const relationshipCoachIds = new Set(
    value.eligibleRelationshipKeys.map((key) => key.coachId),
  );
  const relationshipLeaderIds = new Set(
    value.eligibleRelationshipKeys.map((key) => key.leaderId),
  );
  const hasExactRelationshipLeaderSet =
    relationshipLeaderIds.size === eligibleLeaderIds.size &&
    [...eligibleLeaderIds].every((leaderId) =>
      relationshipLeaderIds.has(leaderId),
    );
  if (
    eligibleLeaderIds.size !== value.eligibleLeaderIds.length ||
    new Set(relationshipSignatures).size !== relationshipSignatures.length ||
    !hasExactRelationshipLeaderSet ||
    (eligibleLeaderIds.size > 0 && relationshipCoachIds.size !== 1)
  ) {
    return false;
  }

  if (value.state !== "ready" && value.state !== "partial") {
    if (
      (value.state === "idle" ||
        value.state === "no_leaders" ||
        value.state === "no_active_relationships") &&
      (eligibleLeaderIds.size > 0 || value.eligibleRelationshipKeys.length > 0)
    ) {
      return false;
    }
    return (
      value.dimensions === null &&
      value.recordedContextCoverage === null &&
      value.leaderPerspectiveState === null
    );
  }

  if (
    !isCanonicalUtcTimestamp(value.asOf) ||
    value.errorCode !== null ||
    !isDenseDataArray(value.dimensions) ||
    value.dimensions.length !== DIMENSION_ORDER.length ||
    !value.dimensions.every(
      (item) =>
        isRecord(item) &&
        hasExactOwnDataProperties(item, DIMENSION_KEYS) &&
        typeof item.dimension === "string" &&
        DIMENSION_NAMES.has(item.dimension as CoachHealthDimensionName) &&
        typeof item.state === "string" &&
        DIMENSION_STATES.has(item.state) &&
        isStringArray(item.matchingLeaderIds) &&
        item.matchingLeaderIds.every(isUuid) &&
        isSourceIssueArray(item.sourceIssues) &&
        isEvidenceItemArray(item.evidence, false, value.asOf),
    ) ||
    new Set(value.dimensions.map((item) => item.dimension)).size !==
      DIMENSION_ORDER.length ||
    !isRecord(value.recordedContextCoverage) ||
    !hasExactOwnDataProperties(value.recordedContextCoverage, COVERAGE_KEYS) ||
    typeof value.recordedContextCoverage.state !== "string" ||
    !DIMENSION_STATES.has(value.recordedContextCoverage.state) ||
    !isStringArray(value.recordedContextCoverage.matchingLeaderIds) ||
    !value.recordedContextCoverage.matchingLeaderIds.every(isUuid) ||
    !isSourceIssueArray(value.recordedContextCoverage.sourceIssues) ||
    !value.recordedContextCoverage.sourceIssues.every((issue) =>
      isCoachHealthRecordedContextSourceType(issue.sourceType),
    ) ||
    !isEvidenceItemArray(
      value.recordedContextCoverage.evidence,
      true,
      value.asOf,
    )
  ) {
    return false;
  }

  if (value.leaderPerspectiveState !== "unavailable") return false;

  const philosophyDimension = value.dimensions.find(
    (item) => item.dimension === "philosophy_and_evidence",
  );
  const continuityDimension = value.dimensions.find(
    (item) => item.dimension === "continuity",
  );
  const supportDimension = value.dimensions.find(
    (item) => item.dimension === "support_and_challenge",
  );
  const reflectionDimension = value.dimensions.find(
    (item) => item.dimension === "reflective_adaptation",
  );
  const philosophyIssues = value.sourceIssues.filter(
    (issue) => issue.sourceType === "coach_philosophy",
  );
  const continuityIssues = value.sourceIssues.filter(
    (issue) => issue.sourceType !== "coach_philosophy",
  );
  if (
    !philosophyDimension ||
    !continuityDimension ||
    !supportDimension ||
    !reflectionDimension ||
    !hasExactSourceIssues(philosophyIssues, philosophyDimension.sourceIssues) ||
    !hasExactSourceIssues(continuityIssues, continuityDimension.sourceIssues) ||
    supportDimension.sourceIssues.length > 0 ||
    reflectionDimension.sourceIssues.length > 0 ||
    (value.state === "ready" && value.sourceIssues.length > 0)
  ) {
    return false;
  }

  if (
    eligibleLeaderIds.size === 0 &&
    !value.exclusionCounts.some(
      (item) => item.reason === "access_unavailable" && item.count > 0,
    )
  ) {
    return false;
  }

  const qualifyingTopLevelIssues = value.sourceIssues.filter((issue) =>
    isCoachHealthRecordedContextSourceType(issue.sourceType),
  );
  if (
    !hasExactSourceIssues(
      qualifyingTopLevelIssues,
      value.recordedContextCoverage.sourceIssues,
    )
  ) {
    return false;
  }

  const hasRecordedContextEvidence =
    value.recordedContextCoverage.evidence.length > 0;
  const isTaskOneEmptyPortfolioState =
    value.eligibleLeaderIds.length === 0 &&
    !hasRecordedContextEvidence &&
    value.recordedContextCoverage.sourceIssues.length === 0 &&
    value.recordedContextCoverage.state === "unavailable";
  if (
    !isTaskOneEmptyPortfolioState &&
    value.recordedContextCoverage.state !==
      expectedRecordedContextState(
        hasRecordedContextEvidence,
        value.recordedContextCoverage.sourceIssues,
      )
  ) {
    return false;
  }

  const hasEmptyUnavailableDimension = (
    dimension: CoachHealthDimensionResult,
  ) =>
    dimension.state === "unavailable" &&
    dimension.matchingLeaderIds.length === 0 &&
    dimension.sourceIssues.length === 0 &&
    dimension.evidence.length === 0;
  if (isTaskOneEmptyPortfolioState) {
    if (!value.dimensions.every(hasEmptyUnavailableDimension)) return false;
  } else {
    const expectedPhilosophyIssueState = philosophyIssues.some(
      (issue) => issue.state === "error",
    )
      ? "error"
      : philosophyIssues.length > 0
        ? "unavailable"
        : null;
    const philosophyIsValid =
      philosophyDimension.evidence.length === 0 &&
      philosophyDimension.matchingLeaderIds.length === 0 &&
      (expectedPhilosophyIssueState
        ? philosophyDimension.state === expectedPhilosophyIssueState
        : philosophyDimension.state === "not_yet_available" ||
          philosophyDimension.state === "insufficient_recorded_context");
    const supportIsValid = hasEmptyUnavailableDimension(supportDimension);
    const reflectionIsValid = hasEmptyUnavailableDimension(reflectionDimension);
    const continuityIsValid =
      continuityDimension.state ===
        expectedRecordedContextState(
          continuityDimension.evidence.length > 0,
          continuityDimension.sourceIssues,
        ) &&
      matchingSetEqualsEvidenceLeaders(
        continuityDimension.matchingLeaderIds,
        continuityDimension.evidence,
      ) &&
      hasUniqueRelationshipEvents(continuityDimension.evidence) &&
      continuityDimension.evidence.every((item) =>
        eligibleLeaderIds.has(item.leaderId),
      );
    if (
      !philosophyIsValid ||
      !supportIsValid ||
      !reflectionIsValid ||
      !continuityIsValid
    ) {
      return false;
    }
  }

  const recordedContextEventKeys = value.recordedContextCoverage.evidence.map(
    (item) => `${item.leaderId}:${item.eventKey}`,
  );
  const continuityEventKeys = new Set(
    continuityDimension.evidence.map(
      (item) => `${item.leaderId}:${item.eventKey}`,
    ),
  );
  if (
    new Set(recordedContextEventKeys).size !== recordedContextEventKeys.length ||
    recordedContextEventKeys.some((eventKey) => !continuityEventKeys.has(eventKey))
  ) {
    return false;
  }

  const matchingLeaderIds = new Set(
    value.recordedContextCoverage.matchingLeaderIds,
  );
  const matchingSetIsEligible = [...matchingLeaderIds].every((leaderId) =>
    eligibleLeaderIds.has(leaderId),
  );
  if (!matchingSetIsEligible) return false;

  const evidenceLeaderIds = value.recordedContextCoverage.evidence.map(
    (item) => item.leaderId,
  );
  return (
    evidenceLeaderIds.every(
      (leaderId) =>
        eligibleLeaderIds.has(leaderId) && matchingLeaderIds.has(leaderId),
    ) &&
    [...matchingLeaderIds].every((leaderId) =>
      evidenceLeaderIds.includes(leaderId),
    )
  );
}

function compact(
  statement: string,
  actionLabel: CoachHealthCompactPresentation["actionLabel"],
  snapshotNote: string | null = null,
  showFraction = false,
): CoachHealthCompactPresentation {
  return { label: LABEL, statement, snapshotNote, actionLabel, showFraction };
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function hasExclusion(
  profile: CoachHealthEvidenceProfileActive,
  reason: "unresolved_profile" | "access_unavailable",
): boolean {
  return profile.exclusionCounts.some(
    (item) => item.reason === reason && item.count > 0,
  );
}

function hasQualifyingSourceIssue(profile: CoachHealthEvidenceProfileActive): boolean {
  return profile.recordedContextCoverage.sourceIssues.some((issue) =>
    isCoachHealthRecordedContextSourceType(issue.sourceType),
  );
}

function canShowRecordedContextFraction(
  profile: CoachHealthEvidenceProfileActive,
): boolean {
  const eligibleLeaderIds = new Set(unique(profile.eligibleLeaderIds));
  const matchingLeaderIds = unique(
    profile.recordedContextCoverage.matchingLeaderIds,
  );

  return (
    eligibleLeaderIds.size > 0 &&
    profile.recordedContextCoverage.state === "evidence_available" &&
    !hasQualifyingSourceIssue(profile) &&
    !hasExclusion(profile, "unresolved_profile") &&
    !hasExclusion(profile, "access_unavailable") &&
    matchingLeaderIds.length > 0 &&
    matchingLeaderIds.every((leaderId) => eligibleLeaderIds.has(leaderId))
  );
}

function formatDate(
  value: string | null,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  try {
    return new Intl.DateTimeFormat("en-US", { ...options, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: "UTC",
    }).format(date);
  }
}

function formatSnapshotNote(
  profile: CoachHealthEvidenceProfileActive,
  input: CoachHealthPresentationInput,
  denominator: number,
  includePortfolioCount: boolean,
): string | null {
  const date = formatDate(profile.asOf, input.timeZone, {
    month: "short",
    day: "numeric",
  });
  if (!date) return null;
  if (input.freshness === "refreshing") {
    return `Refreshing · Evidence as of ${date}`;
  }
  return includePortfolioCount
    ? `Based on ${denominator} active relationships · Evidence as of ${date}`
    : `Evidence as of ${date}`;
}

function stateLabel(
  key: CoachHealthDimensionName,
  state: CoachHealthDimensionState,
): string {
  if (key === "philosophy_and_evidence" && state === "evidence_available") {
    return "Context only";
  }
  switch (state) {
    case "evidence_available":
      return "Evidence available";
    case "not_yet_available":
      return "Not yet available";
    case "insufficient_recorded_context":
      return "Insufficient recorded context";
    case "partial":
      return "Partial";
    case "unavailable":
      return "Unavailable";
    case "error":
      return "Error";
  }
}

function unavailableSourceSuffix(
  state: CoachHealthDimensionState,
  sourceIssues: readonly CoachHealthPublicSourceIssue[],
): string {
  if (!new Set(["partial", "unavailable", "error"]).has(state)) return "";
  const labels = unique(
    sourceIssues
      .map(
        (issue) =>
          getCoachHealthEvidenceSourceDefinition(issue.sourceType)?.publicLabel,
      )
      .filter((label): label is string => Boolean(label)),
  );
  if (labels.length === 0) return "";
  const noun = labels.length === 1 ? "source" : "sources";
  return ` Unavailable ${noun}: ${labels.join(", ")}.`;
}

function dimensionExplanation(
  key: CoachHealthDimensionName,
  state: CoachHealthDimensionState,
  sourceIssues: readonly CoachHealthPublicSourceIssue[],
): string {
  const copy: Record<CoachHealthDimensionName, Record<CoachHealthDimensionState, string>> = {
    philosophy_and_evidence: {
      evidence_available:
        "The saved philosophy is available for reflection, but current sources do not prove alignment in practice.",
      not_yet_available: "No saved philosophy context is available yet.",
      insufficient_recorded_context:
        "There is not enough recorded context to reflect on philosophy in practice.",
      partial:
        "Some philosophy context may be available, but the current source view is incomplete.",
      unavailable:
        "No saved philosophy context is available from current admitted sources.",
      error: "Saved philosophy context is temporarily unavailable.",
    },
    support_and_challenge: {
      evidence_available:
        "Source-linked context is available, but current admitted sources cannot safely attribute support or challenge patterns or their effect on the leader.",
      not_yet_available:
        "Current admitted sources do not yet provide support or challenge evidence.",
      insufficient_recorded_context:
        "Current admitted sources do not provide enough context to assess support or challenge.",
      partial:
        "Some support and challenge source context is unavailable, so no pattern or effect is inferred.",
      unavailable:
        "Support and challenge evidence is unavailable; current admitted sources cannot safely attribute patterns or effects.",
      error: "Support and challenge evidence is temporarily unavailable.",
    },
    continuity: {
      evidence_available:
        "Qualifying sources show recorded coaching context. One artifact does not prove continuity across conversations.",
      not_yet_available:
        "No qualifying recorded coaching context is available yet.",
      insufficient_recorded_context:
        "Current sources do not provide enough recorded coaching context.",
      partial:
        "Some qualifying recorded coaching context is available, but the current source view is incomplete. One artifact does not prove continuity across conversations.",
      unavailable:
        "Recorded coaching context is unavailable from current admitted sources.",
      error: "Recorded coaching context is temporarily unavailable.",
    },
    reflective_adaptation: {
      evidence_available:
        "Source-linked reflection context is available, but current admitted sources do not prove that a later coaching move resulted from reflection.",
      not_yet_available:
        "No source-linked reflective adaptation context is available yet.",
      insufficient_recorded_context:
        "Current sources do not provide enough context to connect reflection to a later coaching move.",
      partial:
        "Some reflective adaptation source context is unavailable, so no later effect is inferred.",
      unavailable:
        "Current admitted sources do not prove that a later coaching move resulted from reflection.",
      error: "Reflective adaptation context is temporarily unavailable.",
    },
  };

  return `${copy[key][state]}${unavailableSourceSuffix(state, sourceIssues)}`;
}

function unrelatedSourceIssueNotice(
  sourceIssues: readonly CoachHealthPublicSourceIssue[],
): string {
  const labels = unique(
    sourceIssues
      .filter(
        (issue) => !isCoachHealthRecordedContextSourceType(issue.sourceType),
      )
      .map(
        (issue) =>
          getCoachHealthEvidenceSourceDefinition(issue.sourceType)?.publicLabel,
      )
      .filter((label): label is string => Boolean(label)),
  );
  if (labels.length === 0) return "";
  return labels.length === 1
    ? ` Other coaching source unavailable: ${labels[0]}. This does not change the recorded-context count.`
    : ` Other coaching sources unavailable: ${labels.join(", ")}. These do not change the recorded-context count.`;
}

function buildDimensions(
  profile: CoachHealthEvidenceProfileActive,
): readonly CoachHealthWorkspaceDimensionPresentation[] {
  return DIMENSION_ORDER.map((key) => {
    const dimension = profile.dimensions.find((item) => item.dimension === key);
    const state = key === "continuity"
      ? profile.recordedContextCoverage.state
      : (dimension?.state ?? "unavailable");
    const sourceIssues = key === "continuity"
      ? profile.recordedContextCoverage.sourceIssues
      : (dimension?.sourceIssues ?? []);
    return {
      key,
      ...DIMENSION_COPY[key],
      stateLabel: stateLabel(key, state),
      explanation:
        dimensionExplanation(key, state, sourceIssues) +
        (key === "continuity"
          ? unrelatedSourceIssueNotice(profile.sourceIssues)
          : ""),
    };
  });
}

function isCanonicalEvidenceRoute(item: CoachHealthEvidenceProfileItem): boolean {
  try {
    if (!isCanonicalCoachHealthEvidenceRoute(item.route, {
      sourceType: item.sourceType,
      sourceId: item.sourceId,
      leaderId: item.leaderId,
      relationshipRevision: item.relationshipRevision,
      routeBindingId: item.routeBindingId,
    })) {
      return false;
    }
    const url = new URL(item.route, "https://coach-veera.invalid");

    switch (item.sourceType) {
      case "completed_coach_call":
        return (
          item.routeBindingId === item.sourceId &&
          url.pathname === `/coach/meetings/${item.sourceId}` &&
          item.eventKey === `coach_call:${item.sourceId}`
        );
      case "coach_first_take": {
        const match = url.pathname.match(/^\/coach\/meetings\/([^/]+)\/reflect$/);
        const callId = match ? decodeURIComponent(match[1]) : null;
        return (
          isUuid(callId) &&
          item.routeBindingId === callId &&
          item.eventKey === `coach_call:${callId}`
        );
      }
      case "coach_reviewable_transcript":
        return (
          isUuid(item.sourceId) &&
          item.routeBindingId === item.sourceId &&
          url.pathname === "/coach/reviews"
        );
      case "coach_transcript_session_record": {
        const match = url.pathname.match(/^\/coach\/post-meeting\/([^/]+)$/);
        if (!match) return false;
        const transcriptId = decodeURIComponent(match[1]);
        if (!isUuid(transcriptId)) return false;
        return (
          item.routeBindingId === transcriptId &&
          item.eventKey.startsWith("coach_call:")
        );
      }
      case "coaching_continuity_moment": {
        const meeting = url.pathname.match(
          /^\/coach\/meetings\/([^/]+)$/,
        );
        if (meeting) {
          const callId = decodeURIComponent(meeting[1]);
          return (
            isUuid(callId) &&
            item.eventKey === `coach_call:${callId}`
          );
        }
        const reflection = url.pathname.match(
          /^\/coach\/meetings\/([^/]+)\/reflect$/,
        );
        if (reflection) {
          const callId = decodeURIComponent(reflection[1]);
          return (
            isUuid(callId) &&
            item.routeBindingId === callId &&
            item.eventKey === `coach_call:${callId}`
          );
        }
        return false;
      }
    }
  } catch {
    return false;
  }
  return false;
}

function buildEvidenceRows(
  profile: CoachHealthEvidenceProfileActive,
  input: CoachHealthPresentationInput,
  matchingLeaderIds: ReadonlySet<string>,
): readonly CoachHealthWorkspaceEvidencePresentation[] {
  const eligibleLeaderIds = new Set(unique(profile.eligibleLeaderIds));
  return profile.recordedContextCoverage.evidence
    .filter(
      (item) =>
        eligibleLeaderIds.has(item.leaderId) &&
        matchingLeaderIds.has(item.leaderId),
    )
    .map((item) => {
      const leaderLabel = authenticatedLeaderLabel(
        input.leaderLabelsById[item.leaderId],
      );
      const definition = getCoachHealthEvidenceSourceDefinition(item.sourceType);
      const timestamp = formatDate(item.evidenceAt, input.timeZone, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
      const canonicalRoute = isCanonicalEvidenceRoute(item) ? item.route : null;

      return {
        sourceLabel: definition?.publicLabel ?? SOURCE_UNAVAILABLE,
        leaderLabel,
        timestampLabel: `${TIMESTAMP_PREFIX[item.sourceType]} ${timestamp ?? "Date unavailable"}`,
        route: input.evidenceActionsEnabled ? canonicalRoute : null,
        unavailableCopy:
          input.evidenceActionsEnabled && canonicalRoute === null
            ? EVIDENCE_DESTINATION_UNAVAILABLE
            : null,
      };
    });
}

function activePresentation(
  profile: CoachHealthEvidenceProfileActive,
  input: CoachHealthPresentationInput,
): CoachHealthPresentation {
  const eligibleLeaderIds = unique(profile.eligibleLeaderIds);
  const eligibleLeaderIdSet = new Set(eligibleLeaderIds);
  const authoritativeMatchingLeaderIds = unique(
    profile.recordedContextCoverage.matchingLeaderIds,
  );
  const matchingSetIsValid = authoritativeMatchingLeaderIds.every((leaderId) =>
    eligibleLeaderIdSet.has(leaderId),
  );
  const matchingLeaderIds = matchingSetIsValid
    ? authoritativeMatchingLeaderIds
    : [];

  if (
    eligibleLeaderIds.length === 0 &&
    hasExclusion(profile, "access_unavailable")
  ) {
    return {
      state: "access_unavailable",
      compact: compact(
        "Coaching context is unavailable with current access",
        null,
      ),
      workspace: null,
      matchingLeaderIds: [],
    };
  }

  const fractionIsComplete = canShowRecordedContextFraction(profile);
  const fractionVisible =
    fractionIsComplete &&
    input.recordedContextFractionEnabled &&
    input.freshness === "fresh";
  const hasIncompletePortfolio =
    hasExclusion(profile, "unresolved_profile") ||
    hasExclusion(profile, "access_unavailable");
  const hasForeignMatchingLeader = !matchingSetIsValid;
  const recordedContextIsUnavailable =
    hasIncompletePortfolio ||
    hasForeignMatchingLeader ||
    hasQualifyingSourceIssue(profile) ||
    ["partial", "unavailable", "error"].includes(
      profile.recordedContextCoverage.state,
    ) ||
    (profile.recordedContextCoverage.state === "evidence_available" &&
      matchingLeaderIds.length === 0);
  const matchingLeaderIdSet = new Set(matchingLeaderIds);
  const evidence = buildEvidenceRows(profile, input, matchingLeaderIdSet);
  const hasVisibleEvidence = evidence.length > 0;

  let statement: string;
  let metricStatement: string;
  let actionLabel: CoachHealthCompactPresentation["actionLabel"];
  if (recordedContextIsUnavailable) {
    statement = "Some coaching context is unavailable";
    metricStatement = statement;
    actionLabel = hasVisibleEvidence ? "View recorded context" : "Retry";
  } else if (fractionVisible) {
    statement = `${matchingLeaderIds.length} of ${eligibleLeaderIds.length} active relationships have recorded coaching context`;
    metricStatement = `Recorded coaching context · ${matchingLeaderIds.length} of ${eligibleLeaderIds.length} active relationships`;
    actionLabel = "View recorded context";
  } else if (hasVisibleEvidence) {
    statement = "Recorded coaching context is available to review";
    metricStatement = statement;
    actionLabel = "View recorded context";
  } else {
    statement = "No qualifying recorded coaching context yet";
    metricStatement = statement;
    actionLabel = "View recorded context";
  }

  return {
    state: profile.state,
    compact: compact(
      statement,
      actionLabel,
      formatSnapshotNote(
        profile,
        input,
        eligibleLeaderIds.length,
        input.recordedContextFractionEnabled && !recordedContextIsUnavailable,
      ),
      fractionVisible,
    ),
    workspace: {
      heading: "Practice reflection",
      audienceLabel: "Private coach reflection",
      privacyLabel: "Not shared with leaders or sponsors",
      metricStatement,
      dimensions: buildDimensions(profile),
      leaderPerspectiveLabel: "Leader perspective unavailable",
      evidence,
      reflectionQuestion: hasVisibleEvidence
        ? "What did you notice in this conversation that might shape your next session?"
        : null,
      retryLabel:
        profile.state === "partial" || recordedContextIsUnavailable
          ? "Retry"
          : null,
    },
    matchingLeaderIds,
  };
}

function errorPresentation(): CoachHealthPresentation {
  return {
    state: "error",
    compact: compact(
      "Coaching Health is temporarily unavailable",
      "Retry",
    ),
    workspace: null,
    matchingLeaderIds: [],
  };
}

export function buildCoachHealthPresentation(
  input: CoachHealthPresentationInput,
): CoachHealthPresentation {
  try {
    const inputSnapshot = snapshotOwnDataTree(input);
    if (!inputSnapshot.ok || !isPresentationInput(inputSnapshot.value)) {
      return errorPresentation();
    }
    const validatedInput = inputSnapshot.value;
    const profile = validatedInput.profile;
    if (!isPresentationProfile(profile)) {
      return errorPresentation();
    }

    switch (profile.state) {
      case "idle":
        return {
          state: "hidden",
          compact: null,
          workspace: null,
          matchingLeaderIds: [],
        };
      case "loading":
        return {
          state: "loading",
          compact: compact("Loading coaching context", null),
          workspace: null,
          matchingLeaderIds: [],
        };
      case "error":
        return errorPresentation();
      case "no_leaders": {
        const unresolved = profile.exclusionCounts.some(
          (item) => item.reason === "unresolved_profile" && item.count > 0,
        );
        return unresolved
          ? {
              state: "resolving_relationships",
              compact: compact(
                "Some leader relationships are still resolving",
                "Retry",
              ),
              workspace: null,
              matchingLeaderIds: [],
            }
          : {
              state: "no_relationships",
              compact: null,
              workspace: null,
              matchingLeaderIds: [],
            };
      }
      case "no_active_relationships":
        return {
          state: "no_active_relationships",
          compact: compact("No active coaching relationships", null),
          workspace: null,
          matchingLeaderIds: [],
        };
      case "access_unavailable":
        return {
          state: "access_unavailable",
          compact: compact(
            "Coaching context is unavailable with current access",
            null,
          ),
          workspace: null,
          matchingLeaderIds: [],
        };
      case "ready":
      case "partial":
        return activePresentation(profile, validatedInput);
      default:
        return errorPresentation();
    }
  } catch {
    return errorPresentation();
  }
}
